import React, { useState } from "react";

const STORAGE_KEY = "qrAutoPurchase.howItWorks.collapsed";

const STEPS = [
  {
    icon: "fa-qrcode",
    title: "QR sticker on the truck",
    text: "Each vehicle listed below gets a QR sticker with its vehicle number, stuck on the windshield.",
  },
  {
    icon: "fa-camera",
    title: "Scanned at the weighbridge",
    text: "A camera at the weighbridge reads the sticker as the truck drives on.",
  },
  {
    icon: "fa-weight-hanging",
    title: "Weighed and photographed",
    text: "Once the truck is fully on the scale and the weight has settled, Revcon records the weight and takes a photo.",
  },
  {
    icon: "fa-file-invoice",
    title: "Purchase recorded",
    text: "The purchase is created with the supplier, product and services set for the vehicle, without anyone typing it in.",
  },
];

function readCollapsed() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch (e) {
    return false;
  }
}

/** Explains the QR Auto-Purchase flow and that the Revcon team must install the scanner first. */
export function QrAutoPurchaseHowItWorks() {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch (e) {
      // Remembering the choice is only a convenience
    }
  };

  return (
    <div className="border border-dashed border-gray-300 rounded p-5 mb-6">
      <div className="d-flex align-items-center justify-content-between cursor-pointer" role="button" onClick={toggle}>
        <div className="fw-bold fs-5 text-gray-800">
          <i className="fa fa-circle-info text-primary me-2" />
          How QR Auto-Purchase works
        </div>
        <span className="text-primary fs-7">
          {collapsed ? "Show" : "Hide"}
          <i className={`fa fa-chevron-${collapsed ? "down" : "up"} ms-2`} />
        </span>
      </div>

      {!collapsed && (
        <>
          <div className="row g-4 mt-1">
            {STEPS.map((step, i) => (
              <div key={step.title} className="col-md-6 col-xl-3">
                <div className="d-flex gap-3 h-100">
                  <div className="symbol symbol-40px flex-shrink-0">
                    <span className="symbol-label bg-light-primary">
                      <i className={`fa ${step.icon} text-primary fs-4`} />
                    </span>
                  </div>
                  <div>
                    <div className="fw-semibold text-gray-800">{i + 1}. {step.title}</div>
                    <div className="text-muted fs-7">{step.text}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <ul className="text-muted fs-7 mt-5 mb-0 ps-5">
            <li>
              <b>1-step:</b> the truck is weighed once (loaded) and its saved empty weight is used to work out
              the net weight.
            </li>
            <li>
              <b>2-step:</b> the truck is weighed loaded when it arrives and again empty when it comes back;
              the purchase is completed on the second weighing.
            </li>
            <li>
              The same truck is not recorded twice within 20 minutes. Anything unusual, such as a missing
              empty weight, a weight that did not settle or a failed photo, is saved as a draft in
              Pending Purchases for your operator to check and complete.
            </li>
          </ul>
        </>
      )}

      <div className="alert alert-light-warning d-flex align-items-start gap-3 fs-7 mt-5 mb-0">
        <i className="fa fa-screwdriver-wrench fs-3 text-warning mt-1" />
        <span>
          <b>Setup required.</b> This feature needs a QR scanner camera installed and configured at your
          weighbridge. Please contact the Revcon team to get the QR scanner installation done before using it.
        </span>
      </div>
    </div>
  );
}
